import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import PageHero from '@/components/feature/PageHero';
import CtaSection from '@/components/feature/CtaSection';
import BewerbungForm from '@/components/feature/BewerbungForm';
import { jobOffer } from '@/mocks/jobs';
import { company } from '@/mocks/company';

const heroImg = '/assets/klob-karriere-hero.jpg';

export default function Karriere() {
  return (
    <div className="min-h-screen bg-background-50 text-foreground-50 font-sans">
      <Header />
      <main>
        <PageHero
          eyebrow="Karriere"
          title={<>Wir suchen <span className="text-primary-500">Verstärkung</span></>}
          subtitle="Du bist Putzer oder Stuckateur und suchst einen fairen, bodenständigen Arbeitgeber? Dann bewirb dich jetzt — ganz unkompliziert."
          image={heroImg}
        />

        {/* STELLENANGEBOT */}
        <section className="relative bg-background-50 py-24 lg:py-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-12 lg:gap-16">
              <div>
                <span className="text-[12px] uppercase tracking-[0.25em] text-primary-400">Stellenangebot</span>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-foreground-50">
                  {jobOffer.title}
                </h2>
                <div className="mt-5 flex flex-wrap items-center gap-3 text-sm text-foreground-400">
                  <span className="inline-flex items-center gap-2">
                    <i className="ri-map-pin-line text-primary-400"></i>{jobOffer.location}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                  <span className="inline-flex items-center gap-2">
                    <i className="ri-briefcase-line text-primary-400"></i>{jobOffer.type}
                  </span>
                </div>
                <p className="mt-6 text-lg leading-relaxed text-foreground-100/85">{jobOffer.intro}</p>

                <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {jobOffer.facts.map((f) => (
                    <div
                      key={f.label}
                      className="rounded-sm border border-background-300/60 bg-background-100 px-4 py-5 transition-all duration-300 hover:border-primary-500/50"
                    >
                      <div className="w-10 h-10 flex items-center justify-center rounded-sm bg-primary-500/15 text-primary-400">
                        <i className={`${f.icon} text-xl`}></i>
                      </div>
                      <p className="mt-3 text-xs uppercase tracking-widest text-foreground-400">{f.label}</p>
                      <p className="mt-1 text-base font-bold text-foreground-50">{f.value}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <a
                    href="#bewerbung"
                    className="inline-flex items-center justify-center h-12 px-8 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold cursor-pointer whitespace-nowrap transition-colors"
                  >
                    Jetzt bewerben <i className="ri-arrow-right-line ml-2"></i>
                  </a>
                  <a
                    href={company.phoneHref}
                    className="inline-flex items-center justify-center h-12 px-8 rounded-md border border-foreground-50/30 hover:bg-foreground-50/10 text-foreground-50 font-semibold cursor-pointer whitespace-nowrap transition-colors"
                  >
                    <i className="ri-phone-line mr-2"></i>{company.phone} anrufen
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BENEFITS */}
        <section className="bg-background-100 border-y border-background-300/40 py-24 lg:py-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-2xl">
              <span className="text-[12px] uppercase tracking-[0.25em] text-primary-400">Deine Vorteile</span>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-foreground-50">
                Das bekommst du <span className="text-primary-500">bei uns</span>
              </h2>
              <p className="mt-4 text-lg text-foreground-400">
                Faire Bezahlung, geregelte Zeiten und ein ehrliches Miteinander — darauf kannst du dich bei uns verlassen.
              </p>
            </div>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {jobOffer.benefits.map((b) => (
                <div
                  key={b.title}
                  className="rounded-sm border border-background-300/60 bg-background-50/40 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-primary-500/50"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-sm bg-primary-500/15 text-primary-400">
                    <i className={`${b.icon} text-2xl`}></i>
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-foreground-50">{b.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-400">{b.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ANFORDERUNGEN */}
        <section className="bg-background-50 py-24 lg:py-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <span className="text-[12px] uppercase tracking-[0.25em] text-primary-400">Das bringst du mit</span>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-foreground-50">
                Kein Lebenslauf & Anschreiben? <span className="text-primary-500">Kein Problem.</span>
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-foreground-100/80">
                Bei uns zählt, wer du bist — nicht, was auf dem Papier steht. Ein kurzer Anruf genügt und wir lernen uns
                persönlich kennen.
              </p>
              <ul className="mt-8 space-y-4">
                {jobOffer.requirements.map((r) => (
                  <li key={r} className="flex items-start gap-3">
                    <span className="mt-1 w-5 h-5 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-400 shrink-0">
                      <i className="ri-check-line text-sm"></i>
                    </span>
                    <span className="text-base leading-relaxed text-foreground-100/85">{r}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 rounded-sm border border-primary-500/30 bg-primary-500/5 px-6 py-5">
                <p className="text-base text-foreground-100/90">
                  <i className="ri-lightbulb-flash-line text-primary-400 mr-2"></i>
                  <strong className="text-foreground-50">Tipp:</strong> Eine Ausbildung als Maler oder Maurer ist von Vorteil —
                  Quereinsteiger sind aber herzlich willkommen.
                </p>
              </div>
            </div>
            <div className="relative rounded-sm border border-background-300/60 bg-background-100 p-8">
              <h3 className="font-display text-2xl font-bold text-foreground-50">Auf einen Blick</h3>
              <dl className="mt-6 divide-y divide-background-300/40">
                {[
                  { k: 'Position', v: 'Putzer / Stuckateur' },
                  { k: 'Eintrittsgehalt', v: '23 € / Stunde' },
                  { k: 'Arbeitsumfang', v: 'Vollzeit · 40 Stunden' },
                  { k: 'Arbeitszeit', v: '6:30 – 15:00 Uhr' },
                  { k: 'Urlaub', v: '30 Tage' },
                  { k: 'Fahrtweg', v: 'wird bezahlt' },
                  { k: 'Lebenslauf & Anschreiben', v: 'nicht nötig' },
                ].map((row) => (
                  <div key={row.k} className="flex items-center justify-between gap-4 py-4">
                    <dt className="text-sm text-foreground-400">{row.k}</dt>
                    <dd className="text-sm font-semibold text-foreground-50 text-right">{row.v}</dd>
                  </div>
                ))}
              </dl>
              <Link
                to="/ueber-uns"
                className="mt-6 inline-flex items-center text-sm font-semibold text-primary-400 hover:text-primary-300 cursor-pointer whitespace-nowrap transition-colors"
              >
                Mehr über uns erfahren <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* BEWERBUNG */}
        <section id="bewerbung" className="relative bg-background-100 border-t border-background-300/40 py-24 lg:py-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <div>
              <span className="text-[12px] uppercase tracking-[0.25em] text-primary-400">Bewerbung</span>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-foreground-50">
                Interesse? <span className="text-primary-500">Melde dich.</span>
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-foreground-400">
                Schick uns kurz deine Kontaktdaten oder ruf einfach an. Wir melden uns und besprechen alles Weitere — ganz
                unkompliziert, ohne Bewerbungsmappe.
              </p>
              <div className="mt-10 space-y-4 rounded-sm border border-background-300/60 bg-background-50/40 p-6">
                <a
                  href={company.phoneHref}
                  className="group flex items-center gap-4 rounded-sm border border-background-300/60 px-4 py-4 transition-colors hover:border-primary-500/50 hover:bg-primary-500/5 cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-sm bg-primary-500/15 text-primary-400">
                    <i className="ri-phone-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-foreground-400">Telefon</p>
                    <p className="text-sm font-semibold text-foreground-50">{company.phone}</p>
                  </div>
                </a>
                <a
                  href={`mailto:${company.email}`}
                  className="group flex items-center gap-4 rounded-sm border border-background-300/60 px-4 py-4 transition-colors hover:border-primary-500/50 hover:bg-primary-500/5 cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-sm bg-primary-500/15 text-primary-400">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-foreground-400">E-Mail</p>
                    <p className="text-sm font-semibold text-foreground-50 break-all">{company.email}</p>
                  </div>
                </a>
                <div className="flex items-center gap-4 rounded-sm border border-background-300/60 px-4 py-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-sm bg-primary-500/15 text-primary-400">
                    <i className="ri-map-pin-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-foreground-400">Standort</p>
                    <p className="text-sm font-semibold text-foreground-50">
                      {company.street}, {company.city}
                    </p>
                  </div>
                </div>
              </div>
              <p className="mt-8 text-sm text-foreground-400">
                <i className="ri-checkbox-circle-line text-primary-400 mr-2"></i>
                Unkompliziert & schnell · Kein Lebenslauf & Anschreiben nötig
              </p>
            </div>

            <BewerbungForm formId="bewerbung-formular" />
          </div>
        </section>

        <CtaSection />
      </main>
      <Footer />
    </div>
  );
}